import { HttpErrorResponse } from '@angular/common/http';
import { ElementRef, inject, Injectable, Signal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CarrierCodesValue, CodeshareCarrierCode } from '@views/common/models';
import { Station } from '@vueling-ng/vy-cdk-searcher';
import { ItineraryTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/composed/basket';
import { LoaderFullScreenService } from '@vueling-ng/vy-ds/src/lib/components/globals/loaders/loader-full-screen';
import { ModalConfig, VyModalService } from '@vueling-ng/vy-ds/src/lib/components/globals/modal';
import { AccountManagerResourcesService } from 'app/services/api-services/accountManager/accountManager.resources';
import {
  SmartTransferResult,
  TransferDataRequest,
  typeTransfer
} from 'app/services/api-services/accountManager/models/transfer-session-model';
import { BookingManagerDataService } from 'app/services/api-services/bookingManager/bookingManager.data-service';
import { SessionOidcService } from 'app/services/external/auth-oidc/session-oidc.service';
import { environment } from 'environments/environment';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import {
  AlertBookingStatus,
  AlertConfig,
  BookingChannelType,
  BookingDetails,
  BookingDetailsSessionData,
  BookingDetailsViewModel,
  breadCrumbItems,
  CabinBagCodes,
  emptyBookingDetailsState,
  englandCountryCode,
  FeaturedCard,
  featuredCards,
  featuredCards_Legacy,
  FlightJourney,
  IAlertConfig,
  IBookingDetailsState,
  improveTripOptionsDisallowed,
  journeyDetailsDisallowed,
  otherManagementOptionsDisallowed,
  Service,
  ServicesPerPassenger
} from '../models/booking-details.model';
import { DownloadPdfService } from './download-pdf.service';

@Injectable({ providedIn: 'root' })
export class BookingDetailsService {
  private readonly _accountManagerDataService = inject(AccountManagerResourcesService);
  private readonly _bookingManagerDataService = inject(BookingManagerDataService);
  private readonly _sessionOidcService = inject(SessionOidcService);
  private readonly _loaderService = inject(LoaderFullScreenService);
  private readonly _modalService = inject(VyModalService);
  private readonly _downloadPdfService = inject(DownloadPdfService);
  private readonly _translateService = inject(TranslateService);

  private readonly _alertConfig = signal<IAlertConfig>(AlertConfig['Default']);
  private readonly _currentState = signal<BookingDetailsSessionData>(emptyBookingDetailsState);
  private readonly _redirectMmb = signal<boolean>(environment.featureFlag.redirectMMB);

  public getEnabledRedirectsMmb = this._redirectMmb.asReadonly();

  public loadViewModel(): Observable<BookingDetailsViewModel> {
    return this._bookingManagerDataService.preparedDataViewModel().pipe(
      map((bookingDetails: IBookingDetailsState) => {
        this._currentState.set({
          language: bookingDetails.state.language,
          stations: bookingDetails.state.stations,
          user: bookingDetails.state.user,
          recordLocator: bookingDetails.state.recordLocator,
          email: bookingDetails.state.email
        });
        const organizationCode = this._sessionOidcService
          .currentSessionOidc()
          .userData.organizationCode?.trim()
          .toUpperCase();
        this._redirectMmb.set(
          environment.featureFlag.redirectMMB &&
            environment.featureFlag.authorizedAccounts.includes(organizationCode || '')
        );
        return this.configViewModel(bookingDetails);
      }),
      tap((bookingDetailsViewModel: BookingDetailsViewModel) => {
        this.resetAlertContent();
        const pnrStatus = bookingDetailsViewModel.bookingDetails.pnrStatus;
        if (pnrStatus === 'PendingRefund' || pnrStatus === 'Refunded' || pnrStatus === 'PendingProcess') {
          this.setAlertContent(pnrStatus);
        }
        const operatedByStatus = this.checkOperatedBy(bookingDetailsViewModel);
        if (operatedByStatus) {
          this.setAlertContent(operatedByStatus);
        }
      }),
      tap(() => {
        this._loaderService.loaderHideEvent$.next();
      }),
      catchError((err: HttpErrorResponse) => {
        this._loaderService.loaderHideEvent$.next();
        return throwError(() => err);
      })
    );
  }

  public openModal(config: ModalConfig): void {
    this._modalService.openModal(config);
  }

  public closeModal(): void {
    this._modalService.closeModal();
  }

  public getCurrentState(): Signal<BookingDetailsSessionData> {
    return this._currentState.asReadonly();
  }

  public getCurrentAlert(): Signal<IAlertConfig> {
    return this._alertConfig.asReadonly();
  }

  public downloadPDF(tripSections: ElementRef<HTMLDivElement>[], recordLocator: string): Observable<void> {
    return this._downloadPdfService.downloadTripInfo(tripSections, recordLocator);
  }

  public transferSessionB2C(sessionData: BookingDetailsSessionData): Observable<SmartTransferResult> {
    const currentLanguage = this._translateService.currentLang;
    const request: TransferDataRequest = {
      recordLocator: sessionData.recordLocator,
      email: sessionData.email,
      cultureCode: currentLanguage,
      type: typeTransfer.Smart
    };
    return this._accountManagerDataService.transferSessionB2C(request).pipe(
      map((response) => ({
        cryptData: response.data,
        culture: currentLanguage
      }))
    );
  }

  public createCookie(): Observable<boolean> {
    return this._accountManagerDataService.createCookie();
  }

  private configViewModel(bookingDetails: IBookingDetailsState): BookingDetailsViewModel {
    return {
      userName: bookingDetails.state.user,
      recordLocator: bookingDetails.state.recordLocator,
      bookingDetails: bookingDetails.booking,
      stations: bookingDetails.state.stations,
      currentLanguage: bookingDetails.state.language,
      currencyCode: bookingDetails.booking.paymentDetails?.payments?.at(0)?.currencyCode,
      itineraryType:
        bookingDetails.booking.flightJourneys?.length > 1 ? ItineraryTypeEnum.RoundTrip : ItineraryTypeEnum.OneWay,
      breadcrumbItems: breadCrumbItems,
      featuredCards: this.loadFeaturedCards(bookingDetails.booking, bookingDetails.state.stations),
      hasGcbg: bookingDetails.booking.servicesPerPassenger.some((servicesPerPax) =>
        this.servicesPerPaxHaveGcbg(servicesPerPax)
      ),
      isGds: bookingDetails.booking.channel === 'GDS',
      isTtoo: bookingDetails.booking.channel === 'TTOO',
      areOtherManagementOptionsAllowed: !otherManagementOptionsDisallowed.includes(bookingDetails.booking.pnrStatus),
      areImproveTripOptionsAllowed:
        !improveTripOptionsDisallowed.includes(bookingDetails.booking.pnrStatus) &&
        !this.isFullyOperatedByThirdParty(bookingDetails.booking.flightJourneys),
      areJourneyDetailsAllowed:
        !journeyDetailsDisallowed.includes(bookingDetails.booking.pnrStatus) &&
        bookingDetails.booking.flightJourneys.length !== 0
    };
  }

  private loadFeaturedCards(booking: BookingDetails, stations: Station[]): FeaturedCard[] {
    const currentCards = this._redirectMmb() ? featuredCards : featuredCards_Legacy;
    return currentCards.map((card) => {
      if (card.page === 'Checkin') {
        return {
          ...card,
          isAllowed: !this.isFullyOperatedByThirdParty(booking.flightJourneys)
        };
      }
      if (card.page === 'SeatChange') {
        return {
          ...card,
          isAllowed: !this.isCodeShare(booking.flightJourneys)
        };
      }
      if (card.page === 'FlightChange' || card.page === 'NameChange') {
        return {
          ...card,
          isAllowed: this.isFlightChangeAllowed(booking.channel, booking.flightJourneys)
        };
      }
      if (card.page === 'UkTravel') {
        return {
          ...card,
          isAllowed: this.isApiTransactionsAvailable(booking.flightJourneys, stations)
        };
      }
      return card;
    });
  }

  private servicesPerPaxHaveGcbg(servicesPerPax: ServicesPerPassenger): boolean {
    return (
      (servicesPerPax.inboundServices?.some((service) => this.serviceIsCabinBag(service)) ||
        servicesPerPax.outboundServices?.some((service) => this.serviceIsCabinBag(service))) ??
      false
    );
  }

  private serviceIsCabinBag(service: Service): boolean {
    return CabinBagCodes.includes(service.serviceName) && service.count > 0;
  }

  private isFullyOperatedByThirdParty(flightJourneys: FlightJourney[]): boolean {
    return flightJourneys.every((journey) => {
      return journey.segments.every((segment) => {
        if (segment.codeShareOperatorName) {
          return segment.codeShareOperatorName !== CarrierCodesValue.Vueling;
        }

        return false;
      });
    });
  }

  private resetAlertContent(): void {
    this._alertConfig.set(AlertConfig['Default']);
  }

  private setAlertContent(status: AlertBookingStatus): void {
    switch (status) {
      case 'PendingRefund': {
        this._alertConfig.set(AlertConfig['PendingRefund']);
        break;
      }
      case 'Refunded': {
        this._alertConfig.set(AlertConfig['Refunded']);
        break;
      }
      case 'PendingProcess': {
        this._alertConfig.set(AlertConfig['PendingProcess']);
        break;
      }
      case 'OperatedIberia': {
        this._alertConfig.set(AlertConfig['OperatedIberia']);
        break;
      }
      case 'OperatedBritish': {
        this._alertConfig.set(AlertConfig['OperatedBritish']);
        break;
      }
    }
  }

  private checkOperatedBy(bookingDetailsViewModel: BookingDetailsViewModel): AlertBookingStatus | null {
    const carrierCode = bookingDetailsViewModel.bookingDetails.flightJourneys
      .flatMap((journey) => journey.segments)
      .map((segment) => segment.codeShareOperatorName)
      .find((carrier) => carrier && CodeshareCarrierCode.includes(carrier));

    if (!carrierCode) {
      return null;
    }

    return carrierCode === CarrierCodesValue.Iberia ? 'OperatedIberia' : 'OperatedBritish';
  }

  private isCodeShare(journeys: FlightJourney[]): boolean {
    return journeys.some((journey) => {
      return journey.segments.some((segment) => segment.codeShareCarrierName);
    });
  }

  private isFlightChangeAllowed(bookingChannel: BookingChannelType, journeys: FlightJourney[]): boolean {
    const channelRestrictions = bookingChannel === 'TTOO' || bookingChannel === 'GDS';
    const operatorRestrictions = journeys.some((journey) => {
      return journey.segments.some(
        (segment) =>
          segment.codeShareCarrierName !== CarrierCodesValue.Vueling &&
          segment.codeShareOperatorName === CarrierCodesValue.Vueling
      );
    });
    return !channelRestrictions && !operatorRestrictions;
  }

  private isApiTransactionsAvailable(journeys: FlightJourney[], stations: Station[]): boolean {
    return journeys.some((journey) =>
      journey.segments.some(
        (segment) =>
          this.isStationFromEngland(stations, segment.iataOrigin) ||
          this.isStationFromEngland(stations, segment.iataDestination)
      )
    );
  }

  private isStationFromEngland(stations: Station[], iataToLookFor: string): boolean {
    const station = stations.find((station) => station.stationCode === iataToLookFor);

    return station ? station.locationDetails.countryCode === englandCountryCode : false;
  }
}
